// apps/client/src/hooks/office/useMonthlySettlementsOffice.ts
// OFFICE DASHBOARD SPECIFIC - Year-based filtering only (no relative periods)
// Separated from main dashboard hooks for independent evolution
// MIGRATED (2026-07-10): Fetches through the shared dashboard query cache (useDashboardQuery).

import { useMemo } from 'react';
import { useDashboardQuery } from '../useDashboardQuery';

interface MonthlySettlement {
  yearMonth: string;
  companyId: string;
  totalSettled: number;
  settlementCount: number;
  positiveSettled: number;
  clawbacks: number;
  totalTopUps: number;
  topUpCount: number;
  updatedAt: any;
  byTemplate?: Record<string, {
    total: number;
    count: number;
    byLender?: Record<string, {
      total: number;
      count: number;
    }>;
  }>;
  byOffice?: Record<string, {
    total: number;
    count: number;
    byTemplate?: Record<string, {
      total: number;
      count: number;
      byLender?: Record<string, {
        total: number;
        count: number;
      }>;
      byBroker: Record<string, {
        brokerDisplay: string;
        total: number;
        count: number;
        avgLoanSize: number;
      }>;
    }>;
    byBroker: Record<string, {
      brokerDisplay: string;
      total: number;
      count: number;
      avgLoanSize: number;
    }>;
  }>;
  byLender?: Record<string, { total: number; count: number }>;
  byBroker?: Record<string, {
    brokerDisplay: string;
    total: number;
    count: number;
    avgLoanSize: number;
  }>;
}

interface UseMonthlySettlementsOfficeParams {
  companyId: string;
  year: number;
  yearType: 'calendar' | 'financial';
  officeId?: string;
  templateId?: string;
  brokerId?: string;
  entityCode?: string;
}

interface MonthlySettlementsResponse {
  months: Array<{
    month: string;
    totalLoanAmountCents: number;
    settlementCount: number;
    totalCommissionCents: number;
    clawbackCount: number;
    clawbackTotalCents: number;
    byBroker?: Record<string, {
      brokerDisplay: string;
      totalLoanAmountCents: number;
      settlementCount: number;
      totalCommissionCents: number;
    }>;
    byLender?: Record<string, {
      totalLoanAmountCents: number;
      settlementCount: number;
    }>;
    byTemplate?: Record<string, {
      totalLoanAmountCents: number;
      settlementCount: number;
    }>;
  }>;
  totals: {
    totalLoanAmount: number;
    totalCommission: number;
    totalCount: number;
    uniqueSettlementCount?: number;
    uniqueSettledCents?: number;
    uniqueByYear?: Record<string, { count: number; cents: number }>;
  };
}

/**
 * Office Dashboard hook for fetching monthly settlement aggregates
 * Uses year-based filtering only (calendar or financial year)
 */
export function useMonthlySettlementsOffice({ companyId, year, yearType, officeId, templateId, brokerId, entityCode }: UseMonthlySettlementsOfficeParams) {
  const { data, loading, error } = useDashboardQuery<MonthlySettlementsResponse>(
    'getMonthlySettlements',
    {
      companyId,
      year,
      yearType,
      officeId,
      templateId,
      brokerId,
      entityCode,
    },
    { enabled: !!companyId }
  );

  const settlements = useMemo<MonthlySettlement[]>(() => {
    if (!data) return [];

    return data.months.map((item) => ({
      yearMonth: item.month,
      companyId,
      totalSettled: item.totalLoanAmountCents,
      settlementCount: item.settlementCount,
      positiveSettled: item.totalLoanAmountCents > 0 ? item.totalLoanAmountCents : 0,
      clawbacks: item.clawbackTotalCents,
      totalTopUps: 0,
      topUpCount: 0,
      updatedAt: null,
      byBroker: item.byBroker ? Object.fromEntries(
        Object.entries(item.byBroker).map(([brokerId, brokerData]) => [
          brokerId,
          {
            brokerDisplay: brokerData.brokerDisplay,
            total: brokerData.totalLoanAmountCents,
            count: brokerData.settlementCount,
            avgLoanSize: brokerData.settlementCount > 0
              ? Math.round(brokerData.totalLoanAmountCents / brokerData.settlementCount)
              : 0,
          },
        ])
      ) : undefined,
      byLender: item.byLender ? Object.fromEntries(
        Object.entries(item.byLender).map(([lender, lenderData]) => [
          lender,
          { total: lenderData.totalLoanAmountCents, count: lenderData.settlementCount },
        ])
      ) : undefined,
      byTemplate: item.byTemplate ? Object.fromEntries(
        Object.entries(item.byTemplate).map(([templateId, templateData]) => [
          templateId,
          { total: templateData.totalLoanAmountCents, count: templateData.settlementCount },
        ])
      ) : undefined,
    }));
  }, [data, companyId]);

  // Extract deduplicated unique totals from backend (fallback to summed values for backward compat)
  const uniqueTotalSettled = data ? (data.totals.uniqueSettledCents ?? data.totals.totalLoanAmount) : 0;
  const uniqueTotalCount = data ? (data.totals.uniqueSettlementCount ?? data.totals.totalCount) : 0;
  const uniqueByYear = data ? data.totals.uniqueByYear : undefined;

  const totalSettled = settlements.reduce((sum, s) => sum + (s.totalSettled || 0), 0);
  const totalCount = settlements.reduce((sum, s) => sum + (s.settlementCount || 0), 0);

  return {
    settlements,
    loading,
    error,
    totalSettled,
    totalCount,
    uniqueTotalSettled,
    uniqueTotalCount,
    uniqueByYear,
  };
}
